import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  Play, Pause, Square, Copy, Check, ExternalLink,
  Volume2, VolumeX, AlertCircle, Lightbulb,
} from 'lucide-react';

import { BytebeatLayout } from '@/components/BytebeatLayout';
import { WaveformVisualizer } from '@/components/WaveformVisualizer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  serializeFragment,
  deserializeFragment,
  detectVariables,
  buildArgs,
} from '@/lib/bytebeatFragment';
import { generateVerificationPhrase } from '@/lib/verificationPhrase';
import { generateCoverDataUrl } from '@/lib/coverImage';
import { testBytebeatCode } from '@/lib/bytebeatEngine';
import { useBytebeatEngine } from '@/hooks/useBytebeatEngine';

const EXAMPLE_BEATS = [
  {
    name: 'Classic Bytebeat',
    bitrate: 8000,
    code: 't*(t>>9|t>>13)&25|t*(t>>12|t>>16)&63',
    args: [],
  },
  {
    name: 'Sierpinski Harmony',
    bitrate: 8000,
    code: '(t*(t>>5|t>>8))>>(t>>16)',
    args: [],
  },
  {
    name: 'Alien Choir',
    bitrate: 8000,
    code: '((t*("36364689"[t>>13&7]&15))/12&128)+((((t>>12)^(t>>12)-2)%11*t)/4|t>>13)&127',
    args: [],
  },
  {
    name: 'Pulse Bass',
    bitrate: 8000,
    code: 't>>4^t>>8^t>>12',
    args: [],
  },
  {
    name: 'Melody with Speed',
    bitrate: 8000,
    code: '(t*((t>>s|t>>10)&33)>>1)',
    args: ['s'],
  },
];

const BITRATES = [4000, 8000, 11025, 16000, 22050, 44100];

export default function EditorPage() {
  const [name, setName] = useState('My Bytebeat');
  const [code, setCode] = useState('t*(t>>9|t>>13)&25|t*(t>>12|t>>16)&63');
  const [bitrate, setBitrate] = useState(8000);
  const [argValues, setArgValues] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  // Load from fragment on first mount only
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    const frag = deserializeFragment(hash);
    if (!frag) return;
    setName(frag.name);
    setCode(frag.code);
    setBitrate(frag.bitrate);
    const vars = detectVariables(frag.code);
    const loaded = buildArgs(vars, frag.argValues);
    const strArgs: Record<string, string> = {};
    Object.entries(loaded).forEach(([k, v]) => { strArgs[k] = String(v); });
    setArgValues(strArgs);
  }, []);

  // Detect variables in current code
  const variables = useMemo(() => detectVariables(code), [code]);

  // Resolved numeric args
  const resolvedArgs = useMemo(() => {
    const result: Record<string, number> = {};
    variables.forEach((v) => {
      const raw = argValues[v];
      const n = parseFloat(raw ?? '0');
      result[v] = isNaN(n) ? 0 : n;
    });
    return result;
  }, [variables, argValues]);

  // Build the serialized fragment whenever inputs change
  const serialized = useMemo(() => {
    if (!name.trim() || !code.trim()) return '';
    const argNums = variables.map((v) => {
      const n = parseFloat(argValues[v] ?? '0');
      return isNaN(n) ? 0 : n;
    });
    return serializeFragment({
      name: name.trim(),
      bitrate,
      code: code.trim(),
      argValues: argNums,
    });
  }, [name, code, bitrate, variables, argValues]);

  const playerUrl = useMemo(() => {
    if (!serialized) return '';
    const base = window.location.origin + '/player';
    return `${base}#${serialized}`;
  }, [serialized]);

  const verificationPhrase = useMemo(() => {
    return generateVerificationPhrase(serialized);
  }, [serialized]);

  const coverDataUrl = useMemo(() => {
    if (!code.trim()) return null;
    try {
      return generateCoverDataUrl(code, name);
    } catch {
      return null;
    }
  }, [code, name]);

  // Validate code on change
  useEffect(() => {
    if (!code.trim()) {
      setCodeError(null);
      return;
    }
    const err = testBytebeatCode(code, resolvedArgs);
    setCodeError(err);
  }, [code, resolvedArgs]);

  const { isPlaying, play, pause, stop, setVolume, volume } = useBytebeatEngine({
    code,
    bitrate,
    args: resolvedArgs,
  });

  const handleCopy = useCallback(async () => {
    if (!playerUrl) return;
    try {
      await navigator.clipboard.writeText(playerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      //
    }
  }, [playerUrl]);

  const loadExample = useCallback((ex: typeof EXAMPLE_BEATS[0]) => {
    setName(ex.name);
    setCode(ex.code);
    setBitrate(ex.bitrate);
    // reset args but keep defaults
    const defaults: Record<string, string> = {};
    ex.args.forEach((a) => { defaults[a] = '4'; });
    setArgValues(defaults);
  }, []);

  useSeoMeta({
    title: 'Editor — Bytebeat Player',
    description: 'Create and share bytebeat compositions.',
  });

  return (
    <BytebeatLayout>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column: editor controls */}
        <div className="space-y-5">
          <div>
            <h1 className="text-2xl font-bold text-primary glow-cyan">Bytebeat Editor</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Write an expression using <code className="font-mono text-accent/80">t</code> as the time counter. The result is masked to 8 bits.
            </p>
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="beat-name" className="text-sm">Track Name</Label>
            <Input
              id="beat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Bytebeat"
              className="bg-background/60 border-border/60 font-medium"
            />
          </div>

          {/* Code */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="beat-code" className="text-sm">Expression</Label>
              {codeError && (
                <span className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Error
                </span>
              )}
              {!codeError && code.trim() && (
                <span className="text-xs text-green-400 flex items-center gap-1">
                  ✓ Valid
                </span>
              )}
            </div>
            <Textarea
              id="beat-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="t*(t>>9|t>>13)&25|t*(t>>12|t>>16)&63"
              className="font-mono text-sm bg-background/60 border-border/60 min-h-[80px] resize-y text-primary/90"
              spellCheck={false}
            />
            {codeError && (
              <p className="text-xs text-destructive/80 font-mono bg-destructive/5 rounded px-2 py-1">
                {codeError}
              </p>
            )}
          </div>

          {/* Bitrate */}
          <div className="space-y-1.5">
            <Label className="text-sm">Sample Rate</Label>
            <Select
              value={String(bitrate)}
              onValueChange={(v) => setBitrate(Number(v))}
            >
              <SelectTrigger className="bg-background/60 border-border/60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BITRATES.map((r) => (
                  <SelectItem key={r} value={String(r)}>
                    {r.toLocaleString()} Hz
                    {r === 8000 && ' (classic)'}
                    {r === 44100 && ' (CD quality)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Variable arguments */}
          {variables.length > 0 && (
            <Card className="border-accent/20 bg-accent/5">
              <CardHeader className="pb-2 pt-4">
                <CardTitle className="text-sm text-accent/80 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Variable Arguments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  These variables are embedded in the link so recipients don't need to input them manually.
                </p>
                {variables.map((v) => (
                  <div key={v} className="flex items-center gap-3">
                    <Label className="w-16 font-mono text-sm flex-shrink-0">{v}</Label>
                    <Input
                      type="number"
                      value={argValues[v] ?? ''}
                      onChange={(e) => setArgValues((prev) => ({ ...prev, [v]: e.target.value }))}
                      placeholder="0"
                      className="bg-background/60 border-border/60 font-mono"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Volume */}
          <div className="flex items-center gap-3">
            <VolumeX className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={[volume]}
              onValueChange={([v]) => setVolume(v)}
              className="flex-1"
            />
            <Volume2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </div>

          {/* Playback */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={stop}
              className="border-border/50"
              title="Stop"
            >
              <Square className="w-4 h-4" />
            </Button>
            <Button
              onClick={isPlaying ? pause : play}
              disabled={!!codeError || !code.trim()}
              className={isPlaying
                ? 'flex-1 bg-primary/20 hover:bg-primary/30 border border-primary/60 text-primary animate-pulse-glow'
                : 'flex-1 bg-primary hover:bg-primary/90 text-primary-foreground'
              }
            >
              {isPlaying
                ? <><Pause className="w-4 h-4 mr-2" />Pause</>
                : <><Play className="w-4 h-4 mr-2 ml-0.5" />Play</>
              }
            </Button>
          </div>

          {/* Examples */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lightbulb className="w-3 h-3" />
              <span>Example beats</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLE_BEATS.map((ex) => (
                <button
                  key={ex.name}
                  onClick={() => loadExample(ex)}
                  className="text-xs px-2.5 py-1 rounded-md border border-border/50 bg-secondary/40 hover:bg-secondary/70 hover:border-primary/30 text-muted-foreground hover:text-foreground transition-all"
                >
                  {ex.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right column: preview & sharing */}
        <div className="space-y-5">
          {/* Cover preview */}
          <div className="relative w-full aspect-square max-w-xs mx-auto rounded-xl overflow-hidden border border-border/50 box-glow-cyan scanlines isolate">
            {coverDataUrl ? (
              <img src={coverDataUrl} alt={name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-sm">Cover Preview</span>
              </div>
            )}
          </div>

          {/* Waveform */}
          <div className="relative rounded-xl overflow-hidden border border-border/50 bg-[#0f0f1a] h-24 scanlines isolate">
            <WaveformVisualizer code={code} args={resolvedArgs} isPlaying={isPlaying} />
          </div>

          {/* Link output */}
          <Card className="border-border/40 bg-card/60">
            <CardHeader className="pb-2 pt-4">
              <CardTitle className="text-sm text-muted-foreground">Shareable Link</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-background/60 border border-border/40 rounded-lg px-3 py-2 font-mono text-xs text-muted-foreground break-all min-h-[3rem] max-h-24 overflow-y-auto">
                {playerUrl || <span className="italic opacity-50">— fill in code and name to generate —</span>}
              </div>

              {/* Verification phrase */}
              <div className="text-xs bg-muted/30 rounded-md px-2.5 py-2 border border-border/30 flex items-start gap-2">
                <span className="text-muted-foreground/50 flex-shrink-0">Phrase:</span>
                <span className="font-mono text-accent/80">{verificationPhrase}</span>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 gap-2 border-border/50 hover:border-primary/40 text-sm"
                  onClick={handleCopy}
                  disabled={!playerUrl}
                >
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>

                <Button
                  variant="outline"
                  className="gap-2 border-border/50 hover:border-accent/40 text-sm"
                  disabled={!playerUrl}
                  onClick={() => window.open(playerUrl, '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                  Open
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Arg badges in link */}
          {variables.length > 0 && (
            <div className="text-xs text-muted-foreground flex flex-wrap gap-1.5 items-center">
              <span>Embedded args:</span>
              {variables.map((v) => (
                <Badge key={v} variant="outline" className="font-mono border-accent/30 text-accent/70">
                  {v}={argValues[v] ?? '0'}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </BytebeatLayout>
  );
}
